"use client";

import { controlSurfaceVariants } from "@/components/ui/control-surface";
import * as React from "react";
import cn from "twsx";

function useCombinedRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return React.useCallback(
    (element: T) => {
      refs.forEach((ref) => {
        if (!ref) return;

        if (typeof ref === "function") {
          ref(element);
        } else {
          (ref as React.MutableRefObject<T>).current = element;
        }
      });
    },
    [refs],
  );
}

/** Use when a person types multiple lines of text. */
const Textarea = ({
  className,
  placeholder,
  autoHeight = true,
  variant,
  mono,
  onInput,
  onKeyDown,
  onChange,
  value,
  ref,
  ...props
}: React.ComponentProps<"textarea"> & {
  autoHeight?: boolean;
  variant?: "default" | "bare";
  mono?: boolean;
  ref?: React.Ref<HTMLTextAreaElement>;
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const combinedRef = useCombinedRefs(textareaRef, ref);

  const adjustHeight = React.useCallback(() => {
    if (!autoHeight || !textareaRef.current) return;

    const textarea = textareaRef.current;
    // The multiline control surface applies `!h-auto`, so the inline height
    // must be set with "important" priority to take effect at all.
    textarea.style.setProperty("height", "auto", "important");
    // scrollHeight excludes borders, but border-box height includes them;
    // without compensation the content area stays short and scrolls.
    const borderHeight = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.setProperty(
      "height",
      `${textarea.scrollHeight + borderHeight}px`,
      "important",
    );
  }, [autoHeight]);

  React.useEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);

  React.useEffect(() => {
    if (!autoHeight || !textareaRef.current) return;

    // Re-measure when the width changes (layout shifts, hidden sections
    // expanding) since wrapping changes the required height.
    let lastWidth = textareaRef.current.offsetWidth;
    const observer = new ResizeObserver(() => {
      const width = textareaRef.current?.offsetWidth ?? 0;
      if (width !== lastWidth) {
        lastWidth = width;
        adjustHeight();
      }
    });
    observer.observe(textareaRef.current);
    return () => observer.disconnect();
  }, [autoHeight, adjustHeight]);

  const handleInput = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoHeight) {
        adjustHeight();
      }

      onInput?.(e);
    },
    [autoHeight, adjustHeight, onInput],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();

        const textarea = e.currentTarget;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = textarea.value;
        const newValue = `${currentValue.substring(0, start)}\n${currentValue.substring(end)}`;

        // Create a synthetic change event
        const syntheticEvent = {
          ...e,
          target: { ...textarea, value: newValue },
          currentTarget: { ...textarea, value: newValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;

        // Call onChange if it exists
        onChange?.(syntheticEvent);

        // Set cursor position after the newline
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 1;
        }, 0);

        // Adjust height after adding newline
        if (autoHeight) {
          setTimeout(adjustHeight, 0);
        }
      }

      onKeyDown?.(e);
    },
    [onChange, onKeyDown, autoHeight, adjustHeight],
  );

  return (
    <textarea
      className={cn(
        controlSurfaceVariants({ kind: "multiline", variant, mono }),
        variant === "bare" && "min-h-0 py-0",
        "resize-none",
        autoHeight && "overflow-hidden",
        className,
      )}
      placeholder={placeholder}
      ref={combinedRef}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onChange={onChange}
      value={value}
      {...props}
    />
  );
};

Textarea.displayName = "Textarea";

export { Textarea };
